import { useState, createContext, useContext, type ReactNode } from 'react';
import { getLoggedInUserOptions, getUserOptions, UserTypeEnum, type FullUserViewModel } from '@generated';
import { useLocalStorage } from '~/shared/hooks/useLocalStorage';
import { useSearchParams } from '~/shared/routing/useSearchParams';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getMsalEmail } from '../msalUtil';

interface IdentityContextModel {
	loggedInUser: FullUserViewModel;
	userIsAnAdmin: () => boolean;
	userIsAClinician: () => boolean;
	handleImpersonateClicked: () => void;
	handleEndImpersonateClicked: () => void;
	getUserInitials: () => string;
	getUserEmailAddress: () => string;
	userIsImpersonating: () => boolean;
	selectedUserType: {
		selectedUserType: UserTypeEnum;
		selectedUserTypeId: number;
		setSelectedUserTypeId: (id: number) => void;
	};
}

const IdentityContext = createContext<IdentityContextModel | undefined>(undefined);

export function IdentityContextProvider({ children }: { children: ReactNode }) {
	const queryParams = useSearchParams();
	const impersonateUser: boolean = queryParams.get('impersonateUser') !== null;
	const [impersonateId, setImpersonateId] = useLocalStorage<string | null>('impersonateId', null);

	// The logged-in user is resolved by secureRouteLoader before the protected shell renders,
	// so we read it from loader data instead of useQuery — no fetch, no loading state here.
	// Users without a Duro record are redirected to /register by the loader, so by the time
	// we get here the user is guaranteed to exist.
	const { data: loggedInUser } = useSuspenseQuery(getLoggedInUserOptions());

	const { data: impersonateUserInfo } = useQuery({
		...getUserOptions(impersonateId!),
		enabled: impersonateId !== null,
		staleTime: 0,
		gcTime: 0
	});
	const validUserTypeIds = [loggedInUser.primaryUserTypeId, ...loggedInUser.additionalUserTypes.map(x => x.id)];
	const [storedUserTypeId, setSelectedUserTypeId] = useLocalStorage<number>('selectedUserTypeId', loggedInUser.primaryUserTypeId);
	const selectedUserTypeId = validUserTypeIds.includes(storedUserTypeId) ? storedUserTypeId : validUserTypeIds[0];
	const [showImpersonateUserDialog, setShowImpersonateUserDialog] = useState(false);

	const getUserInitials = () => {
		return loggedInUser ? loggedInUser.firstName[0] + loggedInUser.lastName[0] : '';
	};

	const getUserEmailAddress = () => {
		return !impersonateUser ? getMsalEmail()! : impersonateUserInfo ? impersonateUserInfo.emailAddress : '';
	};

	const userIsAnAdmin = () => {
		if (!loggedInUser) return false;

		return (
			loggedInUser.primaryUserTypeId === UserTypeEnum.Administrator ||
			loggedInUser.additionalUserTypes.some(x => x.id === UserTypeEnum.Administrator)
		);
	};

	const userIsAClinician = () => {
		if (!loggedInUser) return false;

		return (
			loggedInUser.primaryUserTypeId === UserTypeEnum.Clinician || loggedInUser.additionalUserTypes.some(x => x.id === UserTypeEnum.Clinician)
		);
	};

	const userIsImpersonating = () => {
		return impersonateId !== null;
	};

	const handleImpersonateClicked = () => {
		setShowImpersonateUserDialog(true);
	};

	const handleEndImpersonateClicked = () => {
		setImpersonateId(null);
		history.go(0);
	};

	if (!loggedInUser) {
		// This should never happen: secureRouteLoader either resolves a user or redirects to /register.
		throw new Error('IdentityContextProvider rendered without logged in user');
	}

	return (
		<IdentityContext.Provider
			value={{
				loggedInUser,
				userIsAnAdmin,
				userIsAClinician,
				handleImpersonateClicked,
				handleEndImpersonateClicked,
				getUserInitials,
				getUserEmailAddress,
				userIsImpersonating,
				selectedUserType: {
					selectedUserType: selectedUserTypeId as UserTypeEnum,
					selectedUserTypeId,
					setSelectedUserTypeId
				}
			}}
		>
			{children}
		</IdentityContext.Provider>
	);
}

export function useIdentity() {
	const context = useContext(IdentityContext);
	if (!context) {
		throw new Error('useIdentity must be used within a IdentityContextProvider');
	}
	return context;
}
